"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
import { useMemo, useCallback } from "react";
import { useArtistPreviewModal } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { useCompactMode } from "@/contexts/CompactModeContext";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(67);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge
  } = t0;
  const router = useRouter();
  const compact = useCompactMode();
  const {
    isModalOpen: isArtistPreviewOpen,
    modalInitialTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  const submissionCount = drop.author.active_main_stage_submission_ids.length || 0;
  const hasSubmissions = submissionCount > 0;
  const winnerCount = drop.author.winner_main_stage_drop_ids.length || 0;
  const isWinner = winnerCount > 0;
  const hasActivityBadge = hasSubmissions || isWinner;
  const isWaveCreator = drop.author.is_wave_creator;
  let t1;
  if ($[0] !== router) {
    t1 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNavigation = t1;
  const t2 = `tw-mb-0 tw-font-semibold tw-leading-none ${compact ? "tw-text-sm" : "tw-text-md"}`;
  const t3 = drop.author.handle ?? drop.author.id;
  let t4;
  if ($[2] !== drop.author.handle || $[3] !== drop.author.primary_address || $[4] !== handleNavigation) {
    t4 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[2] = drop.author.handle;
    $[3] = drop.author.primary_address;
    $[4] = handleNavigation;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `/${drop.author.handle ?? drop.author.primary_address}`;
  let t6;
  if ($[6] !== drop.author.handle || $[7] !== t4 || $[8] !== t5) {
    t6 = <Link onClick={t4} href={t5} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{drop.author.handle}</Link>;
    $[6] = drop.author.handle;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <UserProfileTooltipWrapper user={t3}>{t6}</UserProfileTooltipWrapper>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t7) {
    t8 = <p className={t2}>{t7}</p>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== drop.author.level) {
    t9 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[16] = drop.author.level;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== drop.id || $[19] !== handleArtistBadgeClick || $[20] !== hasActivityBadge || $[21] !== submissionCount || $[22] !== winnerCount) {
    t10 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} winCount={winnerCount} onBadgeClick={handleArtistBadgeClick} tooltipId={`header-activity-badge-${drop.id}`} />;
    $[18] = drop.id;
    $[19] = handleArtistBadgeClick;
    $[20] = hasActivityBadge;
    $[21] = submissionCount;
    $[22] = winnerCount;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== drop.id || $[25] !== handleWaveCreatorBadgeClick || $[26] !== isWaveCreator) {
    t11 = isWaveCreator && <WaveCreatorBadge tooltipId={`wave-creator-${drop.id}`} onBadgeClick={handleWaveCreatorBadgeClick} />;
    $[24] = drop.id;
    $[25] = handleWaveCreatorBadgeClick;
    $[26] = isWaveCreator;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== drop.created_at) {
    t13 = <WaveDropTime timestamp={drop.created_at} />;
    $[29] = drop.created_at;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t13 || $[34] !== t8 || $[35] !== t9) {
    t14 = <div className="tw-flex tw-items-center tw-gap-x-2">{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t13;
    $[34] = t8;
    $[35] = t9;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== badge) {
    t15 = !!badge && <div className="tw-ml-2">{badge}</div>;
    $[37] = badge;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t14}{t15}</div></div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== drop.wave || $[43] !== handleNavigation || $[44] !== showWaveInfo) {
    t17 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-leading-0 -tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[42] = drop.wave;
    $[43] = handleNavigation;
    $[44] = showWaveInfo;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t17) {
    t18 = <div>{t17}</div>;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== currentPartIndex || $[49] !== isStorm || $[50] !== partsCount) {
    t19 = isStorm && <div className="tw-relative tw-mt-2 tw-inline-flex"><span className="tw-mb-1.5 tw-text-xs tw-text-iron-50">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[48] = currentPartIndex;
    $[49] = isStorm;
    $[50] = partsCount;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== drop.author || $[53] !== handleArtistModalClose || $[54] !== isArtistPreviewOpen || $[55] !== modalInitialTab) {
    t20 = <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={drop.author} initialTab={modalInitialTab} />;
    $[52] = drop.author;
    $[53] = handleArtistModalClose;
    $[54] = isArtistPreviewOpen;
    $[55] = modalInitialTab;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== drop.author || $[58] !== handleWaveCreatorModalClose || $[59] !== isWaveCreatorPreviewOpen) {
    t21 = <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={drop.author} />;
    $[57] = drop.author;
    $[58] = handleWaveCreatorModalClose;
    $[59] = isWaveCreatorPreviewOpen;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t16 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21) {
    t22 = <>{t16}{t18}{t19}{t20}{t21}</>;
    $[61] = t16;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};
export default WaveDropHeader;